import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Crown, Check, Loader2, CreditCard } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';

interface PremiumModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActivated?: () => void;
}

const FREE_FEATURES = ['月10分の録音時間', '辞書5件まで', 'カスタムタグ5個', '振り返り月1回', 'テーマ1種類'];
const PREMIUM_FEATURES = ['録音時間無制限', '辞書100件', 'カスタムタグ50個', '振り返り月10回', '12テーマ全開放', '画像添付', '優先サポート'];

type Interval = 'month' | 'year';

export default function PremiumModal({ open, onOpenChange, onActivated }: PremiumModalProps) {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<Interval>('month');
  const [checkingOut, setCheckingOut] = useState(false);

  const handleActivate = async () => {
    if (!code.trim()) return;
    setActivating(true);
    try {
      await api.activate(code.trim());
      toast({ title: 'プレミアムプランが有効化されました!' });
      onActivated?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: '招待コードの認証に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setActivating(false);
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const { checkoutUrl } = await api.createCheckout(selectedInterval);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      toast({
        title: '決済ページの取得に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
      setCheckingOut(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            プレミアムプランにアップグレード
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          {/* Free plan */}
          <div className="rounded-lg border p-4 space-y-3">
            <h3 className="font-semibold text-sm">フリー</h3>
            <p className="text-2xl font-bold">¥0</p>
            <ul className="space-y-2">
              {FREE_FEATURES.map(f => (
                <li key={f} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/50 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium plan */}
          <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/5 p-4 space-y-3">
            <h3 className="font-semibold text-sm flex items-center gap-1">
              <Crown className="h-3.5 w-3.5 text-amber-500" />
              プレミアム
            </h3>
            {/* Interval toggle */}
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedInterval('month')}
                className={`flex-1 text-center rounded px-1 py-1 text-xs transition-colors ${
                  selectedInterval === 'month' ? 'bg-amber-500/20 font-bold' : 'hover:bg-accent'
                }`}
              >
                ¥780/月
              </button>
              <button
                onClick={() => setSelectedInterval('year')}
                className={`flex-1 text-center rounded px-1 py-1 text-xs transition-colors ${
                  selectedInterval === 'year' ? 'bg-amber-500/20 font-bold' : 'hover:bg-accent'
                }`}
              >
                ¥6,500/年
              </button>
            </div>
            <ul className="space-y-2">
              {PREMIUM_FEATURES.map(f => (
                <li key={f} className="text-xs flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 mt-0.5 text-amber-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-3 mt-2">
          {/* Stripe Checkout */}
          <Button
            className="w-full gap-2"
            onClick={handleCheckout}
            disabled={checkingOut}
          >
            {checkingOut ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            {selectedInterval === 'year' ? '7日間無料で試す' : 'プレミアムを始める'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            {selectedInterval === 'year'
              ? '無料期間後、年額 ¥6,500。いつでもキャンセル可。'
              : '月額 ¥780。いつでもキャンセル可。'}
          </p>

          {/* Activation code section */}
          {!showCodeInput ? (
            <button
              className="w-full text-xs text-center text-muted-foreground hover:text-foreground transition-colors py-1"
              onClick={() => setShowCodeInput(true)}
            >
              招待コードをお持ちの方
            </button>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="招待コードを入力"
                value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleActivate()}
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowCodeInput(false)}>
                  戻る
                </Button>
                <Button className="flex-1" onClick={handleActivate} disabled={activating || !code.trim()}>
                  {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : '有効化'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
